import api from '../api'

const DEFAULT_CREATIVE_UNIT_PAGE_SIZE = 50
const DEFAULT_CREATIVE_UNIT_PAGE_SIZE_STORE_KEY = 'creativeUnits:pager:pageSize'

const getPagerSizeFromLocalStorage = () => {
  return JSON.parse(localStorage
    ? (localStorage.getItem(DEFAULT_CREATIVE_UNIT_PAGE_SIZE_STORE_KEY) || DEFAULT_CREATIVE_UNIT_PAGE_SIZE)
    : DEFAULT_CREATIVE_UNIT_PAGE_SIZE)
}

const pagerCreativeUnitsDefaultState = {
  page: 1,
  pageSize: getPagerSizeFromLocalStorage(),
  count: undefined,
  pagesCount: undefined,
  itemsPerPage: [5, 10, 25, 50, 100, 200, 500, 1000] // This is a readonly property.
}

const state = {
  all: [],
  creative: null,
  creativeUnits: [],
  creativeUnitsPager: {
    ...pagerCreativeUnitsDefaultState
  },
  creativeUnitsSorting: [],
  creativeUnitsSearchTerms: null,
  creativeUnitBulkEdit: {
    enable: false,
    column: null
  }
}

const actions = {
  getCreatives: ({ state, commit }, id) => {
    return api.getAllCreatives(id).then(creatives => {
      return commit('setAllCreatives', creatives)
    })
  },
  addCreative: ({ state, commit }, newCreative) => {
    return api.addSingleCreative(newCreative).then(creative => {
      commit('setSingleCreative', creative)
      return creative
    })
  },
  deleteCreative: ({ state, commit }, id) => {
    return api.deleteSingleCreative(id)
  },
  updateCreative: ({ state, commit }, data) => {
    return api.updateSingleCreative(data).then(creative => {
      return commit('setSingleCreative', creative)
    })
  },
  editCreativeName: ({ state, commit }, data) => {
    return api.editSingleCreativeName(data).then(creative => {
      return commit('setSingleCreative', creative)
    })
  },
  getCreativeUnits: ({ state, commit }, id) => {
    return api.getAllCreativeUnits(id, {
      pager: state.creativeUnitsPager,
      sorting: state.creativeUnitsSorting,
      searchTerms: state.creativeUnitsSearchTerms
    }).then(creativeUnits => {
      return commit('setAllCreativeUnits', creativeUnits)
    })
  },
  getCreativeUnit: ({ state, commit }, id) => {
    return api.getCreativeUnit(id).then(creativeUnit => {
      return commit('setCreativeUnit', creativeUnit)
    })
  },
  updateCreativeUnit: ({ state, commit }, data) => {
    return api.updateAllCreativeUnits(data, {
      pager: state.creativeUnitsPager,
      sorting: state.creativeUnitsSorting,
      searchTerms: state.creativeUnitsSearchTerms
    }).then(creativeUnits => {
      return commit('setAllCreativeUnits', creativeUnits)
    })
  },
  updateSingleCreativeUnit: ({ state, commit }, data) => {
    return api.updateSingleCreativeUnit(data).then(creativeUnits => {
      return commit('setCreativeUnit', creativeUnits)
    })
  },
  updateBulkCreative: ({ state, commit }, data) => {
    return api.updateBulkCreativeUnits(data)
  },
  uploadCsv: ({ state, commit }, data) => {
    return api.uploadCsvToCreative(data)
  },
  assetCopyToCreative: ({ state, commit }, data) => {
    return api.copyAssetsToCreative(data)
  },
  cloneCreative: ({ state, commit }, data) => {
    return api.cloneSingleCreative(data)
  },
  /**
   * Defines the Creative Units Pager data
   *
   * @param state
   * @param commit
   * @param {Object} pageData
   * @param {number} pageData.page Defines the page number.
   * @param {number} pageData.pageSize Defines the number of items per page.
   * @param {number} pageData.count The actual number of items (units) stored in the database for the given creative.
   * @param {number} pageData.pagesCount The maximum number of pages for the given pageSize and count.
   * @returns {*}
   */
  setCreativeUnitsPagerData: ({state, commit}, pageData) => {
    const {pageSize, ...rest} = pagerCreativeUnitsDefaultState
    return commit('setCreativeUnitsPagerData', pageData || rest)
  },

  /**
   * Defines the Creative units sorting data
   *
   * @param state
   * @param commit
   * @param {Array} sortingData Setting it to null reset the sorting criteria.
   * @param {string} sortingData[0].sortKey Defines the name of the key to sort.
   * @param {string} sortingData[0].sortDirection Defines the sorting direction. If direction is null or undefined the
   *    key is removed from the sorting array.
   * @returns {*}
   */
  setCreativeUnitsSortingData: ({state, commit}, sortingData) => {
    return commit('setCreativeUnitsSortingData', sortingData)
  },

  /**
   * Defines the Creative Units search terms
   *
   * @param state
   * @param commit
   * @param {string} searchTerms search terms keywords
   * @returns {*}
   */
  setCreativeUnitsSearchTerms: ({state, commit}, searchTerms) => {
    return commit('setCreativeUnitsSearchTerms', searchTerms)
  },

  /**
   *
   * @param state
   * @param commit
   * @param enable
   * @param column
   * @return {*}
   */
  setCreativeUnitBulkEditMode: ({state, commit}, {enable, column}) => {
    return commit('setCreativeUnitBulkEditMode', {enable, column})
  },

  /**
   * Call the API and return the review link history related to the creative id.
   *
   * @param state
   * @param commit
   * @param creativeId
   */
  getReviewLinkHistory: ({state, commit}, creativeId) => {
    return api.reviewLInkHistory(creativeId)
  }

}

const mutations = {
  setAllCreatives (state, creatives) {
    state.all = creatives
  },
  setSingleCreative (state, creative) {
    state.creative = creative
  },
  setAllCreativeUnits (state, creativeUnits) {
    state.creativeUnits = creativeUnits
  },
  setCreativeUnit (state, creativeUnits) {
    state.creativeUnits = creativeUnits
  },
  setCreativeUnitsPagerData (state, {page, pageSize, count, pagesCount}) {
    if (localStorage) {
      localStorage.setItem(DEFAULT_CREATIVE_UNIT_PAGE_SIZE_STORE_KEY, pageSize || state.creativeUnitsPager.pageSize)
    }
    state.creativeUnitsPager = {
      page: page || state.creativeUnitsPager.page,
      pageSize: getPagerSizeFromLocalStorage(),
      count: count || state.creativeUnitsPager.count,
      pagesCount: pagesCount || state.creativeUnitsPager.pagesCount,
      itemsPerPage: pagerCreativeUnitsDefaultState.itemsPerPage
    }
  },
  setCreativeUnitsSortingData (state, {sortKey, sortDirection}) {
    const sort = state.creativeUnitsSorting.filter(value => value.indexOf(sortKey) === -1)
    if (sortDirection) {
      sort.unshift(`${sortKey}:${sortDirection}`)
    }
    state.creativeUnitsSorting = sort
  },
  setCreativeUnitsSearchTerms (state, searchTerm) {
    state.creativeUnitsSearchTerms = searchTerm
  },
  setCreativeUnitBulkEditMode (state, data) {
    state.creativeUnitBulkEdit = {...data}
  }
}

const getters = {
  all: state => {
    return state.all
  },
  creative: state => {
    return state.creative
  },
  creativeUnit: state => {
    return state.creativeUnits
  },
  creativeUnits: state => {
    return state.creativeUnits
  },
  creativeUnitsPager: ({creativeUnitsPager}) => creativeUnitsPager,
  creativeUnitsSorting: ({creativeUnitsSorting}) => creativeUnitsSorting,
  creativeUnitsSearchTerms: ({creativeUnitsSearchTerms}) => creativeUnitsSearchTerms,
  creativeUnitBulkEditMode: ({creativeUnitBulkEdit}) => creativeUnitBulkEdit
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/creatives.js